import { injectable } from 'inversify';
import { Collection, Db, getMongoDb } from "@swoft/persistence";
import { InboxItem } from '../../domain/aggregates/InboxItem';
import { GTD_COLLECTIONS } from '../../../../config/collections';

/**
 * MongoDB Repository for InboxItem Aggregate
 * 
 * PRODUCTION IMPLEMENTATION:
 * - Uses UUID as primary key (not MongoDB ObjectId)
 * - Standardized field mapping between database and domain
 * - No leaky abstractions or multiple ID formats
 * - Clean adapter pattern following Eric Evans DDD
 */
@injectable()
export class InboxItemAggregateRepository {
    private db: Db | null = null;
    private collection: Collection | null = null;

    constructor() {
        // Lazy initialization - don't connect until needed
    }

    private ensureConnection() {
        if (!this.db) {
            this.db = getMongoDb();
            this.collection = this.db.collection(GTD_COLLECTIONS.INBOX_ITEMS);
        }
        return this.collection!;
    }

    async save(item: InboxItem): Promise<void> {
        const collection = this.ensureConnection();
        const itemData = item.toData();
        
        // Standardized database schema - SINGLE source of truth
        const doc = {
            _id: item.id, // Use UUID as MongoDB _id for consistency
            originalContent: itemData.originalContent,
            capturedAt: item.capturedAt,
            capturedByPersonId: itemData.capturedByPersonId,
            clarification: itemData.clarification,
            isActionable: itemData.isActionable,
            processingStatus: itemData.processingStatus,
            lastRefinedAt: itemData.lastRefinedAt,
            refinedByPersonId: itemData.refinedByPersonId,
            updatedAt: new Date()
        };

        await collection.replaceOne(
            { _id: item.id as any },
            doc,
            { upsert: true }
        );
    }

    async findById(id: string): Promise<InboxItem | null> {
        const collection = this.ensureConnection();
        const doc = await collection.findOne({ _id: id as any });
        if (!doc) return null;

        return this.mapDocumentToDomain(doc);
    }

    private mapDocumentToDomain(doc: any): InboxItem {
        return InboxItem.fromRepository({
            id: doc._id,
            originalContent: doc.originalContent || '',
            capturedAt: new Date(doc.capturedAt),
            capturedByPersonId: doc.capturedByPersonId,
            clarification: doc.clarification,
            isActionable: doc.isActionable,
            processingStatus: doc.processingStatus || 'unprocessed',
            lastRefinedAt: doc.lastRefinedAt ? new Date(doc.lastRefinedAt) : undefined,
            refinedByPersonId: doc.refinedByPersonId
        });
    }

    async findAll(): Promise<InboxItem[]> {
        const collection = this.ensureConnection();
        const docs = await collection.find({}).toArray();
        return docs.map(doc => this.mapDocumentToDomain(doc));
    }

    async countUnprocessed(): Promise<number> {
        const collection = this.ensureConnection();
        return await collection.countDocuments({
            processingStatus: 'unprocessed'
        });
    }

    async countAll(): Promise<number> {
        const collection = this.ensureConnection();
        return await collection.countDocuments({});
    }

    async delete(id: string): Promise<void> {
        const collection = this.ensureConnection();
        await collection.deleteOne({ _id: id as any });
    }

    async findByFilters(filters: {
        status?: string;
        priority?: string;
        limit?: number;
        offset?: number;
    }): Promise<InboxItem[]> {
        const query: any = {};

        if (filters.status && filters.status !== 'all') {
            query.processingStatus = filters.status === 'processed' ? 'processed' : 'unprocessed';
        }
        const collection = this.ensureConnection();
        const docs = await collection
            .find(query)
            .sort({ capturedAt: -1 })
            .skip(filters.offset || 0)
            .limit(filters.limit || 20)
            .toArray();

        return docs.map(doc => this.mapDocumentToDomain(doc));
    }

    async countByFilters(filters: {
        status?: string;
        priority?: string;
    }): Promise<number> {
        const query: any = {};

        if (filters.status && filters.status !== 'all') {
            query.processingStatus = filters.status === 'processed' ? 'processed' : 'unprocessed';
        }

        const collection = this.ensureConnection();
        return await collection.countDocuments(query);
    }
}
